import { useState, useEffect, useCallback } from 'react';
import { getMe, signup as apiSignup, updateMe as apiUpdateMe, updateCss as apiUpdateCss } from '../api/portalApi';
import type { CustomerProfile, License } from '../api/portalApi';

interface CustomerState {
  customer: CustomerProfile | null;
  licenses: License[];
  loading: boolean;
  error: string | null;
  needsSignup: boolean;
}

export function useCustomer() {
  const [state, setState] = useState<CustomerState>({
    customer: null,
    licenses: [],
    loading: true,
    error: null,
    needsSignup: false,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getMe();
      setState({
        customer: data.customer,
        licenses: data.licenses,
        loading: false,
        error: null,
        needsSignup: false,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load';
      // 404 means customer hasn't signed up yet
      if (msg.includes('not found') || msg.includes('signup')) {
        setState({ customer: null, licenses: [], loading: false, error: null, needsSignup: true });
      } else {
        setState({ customer: null, licenses: [], loading: false, error: msg, needsSignup: false });
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const signup = useCallback(async (data: { name: string; company?: string; plugins: string[] }) => {
    // Sign up with the first plugin (creates customer + license)
    const first = await apiSignup({ name: data.name, company: data.company, plugin: data.plugins[0]! });
    const licenses = [first.license];
    // Add remaining plugins
    for (let i = 1; i < data.plugins.length; i++) {
      const result = await apiSignup({ name: data.name, company: data.company, plugin: data.plugins[i]! });
      licenses.push(result.license);
    }
    setState({
      customer: first.customer,
      licenses,
      loading: false,
      error: null,
      needsSignup: false,
    });
    return first;
  }, []);

  const updateProfile = useCallback(async (data: { name?: string; phone?: string; company?: string }) => {
    const updated = await apiUpdateMe(data);
    setState((s) => ({ ...s, customer: updated }));
  }, []);

  const updateCss = useCallback(async (css: string) => {
    const updated = await apiUpdateCss(css);
    setState((s) => ({ ...s, customer: updated }));
  }, []);

  return {
    ...state,
    reload: load,
    signup,
    updateProfile,
    updateCss,
  };
}
